import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Tag,
  Tooltip,
  PageHeader,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  rackRegistryListService,
  rackRegistryDeleteService,
} from 'services/rackregistry';
import { MyBreadcrumb } from 'utils';
import NetworkToolWrapper from '../../layouts/NetworkToolWrapper';
import { useRackDiagramBreadcrumb } from '../../hooks/useRackDiagramBreadcrumb';

const { Title } = Typography;

const RackRegistryIndex = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const breadcrumbItems = useRackDiagramBreadcrumb();

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await rackRegistryListService();
      // Ensure we're getting an array and each item has a unique key
      const formattedData = Array.isArray(response.data.items)
        ? response.data.items.map((item) => ({
            ...item,
            key: item._id, // Ensure each row has a unique key
          }))
        : [];
      setData(formattedData);
    } catch (error) {
      message.error('Failed to fetch rack registry data');
      setData([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    try {
      await rackRegistryDeleteService(id);
      message.success('Rack registry deleted successfully');
      fetchData();
    } catch (error) {
      message.error('Failed to delete rack registry');
    }
  };

  const getAlertStatus = (record) => {
    if (record.status === 'broken') {
      return { color: 'red', text: 'Equipment is broken' };
    } else if (record.status === 'to_be_removed') {
      return { color: 'orange', text: 'Equipment scheduled for removal' };
    } else if (record.status === 'to_be_retired') {
      return { color: 'orange', text: 'Equipment scheduled for retirement' };
    } else if (record.status === 'to_be_installed') {
      return { color: 'blue', text: 'Equipment pending installation' };
    } else if (record.status === 'removed') {
      return { color: 'gray', text: 'Equipment has been removed' };
    } else if (record.status === 'retired') {
      return { color: 'gray', text: 'Equipment has been retired' };
    }
    return null;
  };

  const columns = [
    {
      title: 'Rack Name',
      dataIndex: 'rack_name',
      key: 'rack_name',
      sorter: (a, b) => a.rack_name.localeCompare(b.rack_name),
    },
    {
      title: 'Total U',
      dataIndex: 'total_u',
      key: 'total_u',
      render: (value) => `${value}U`,
      sorter: (a, b) => a.total_u - b.total_u,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => (text ? new Date(text).toLocaleString() : 'N/A'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              type="primary"
              shape="circle"
              onClick={() => history.push(`/rackregistry/view/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              type="primary"
              shape="circle"
              onClick={() => history.push(`/rackregistry/update/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Upload Photos">
            <Button
              icon={<UploadOutlined />}
              type="primary"
              shape="circle"
              onClick={() => history.push(`/rackregistry/upload/${record._id}`)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this rack registry?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} danger shape="circle" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <NetworkToolWrapper isBranchClientRequired={false}>
      <MyBreadcrumb items={breadcrumbItems} />
      <PageHeader
        ghost={false}
        onBack={() => window.history.back()}
        title="Rack Diagram"
        subTitle=""
        extra={[
          <Button
            key="create"
            type="primary"
            onClick={() => history.push('/rackregistry/create')}
          >
            Create New Rack
          </Button>,
        ]}
      >
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="_id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </PageHeader>
    </NetworkToolWrapper>
  );
};

export default RackRegistryIndex;
